import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import uzCatalog from "./uz-catalog.json";

export type Language = "ru" | "uz";

export const dictionary = {
  ru: {
    dashboard: "Обзор", orders: "Заказы", clients: "Клиенты", contracts: "Договоры", products: "Модели и варианты",
    materials: "Материалы и поставщики", bom: "BOM и нормы", requirements: "Потребность и дефицит", procurement: "Закупки",
    receipts: "Приемка материалов", inventory: "Остатки и резервы", stockOperations: "Складские операции", inventoryCounts: "Инвентаризация",
    routings: "Технологические маршруты", productionOrders: "Производственные заказы", dispatcher: "Диспетчерская",
    cutting: "Раскрой", sewing: "Пошив", productionWip: "WIP и простои", quality: "ОТК и приемка",
    defects: "Дефекты", rework: "Доработка", packaging: "Упаковка и QR", finishedGoods: "Готовая продукция", shipments: "Отгрузки",
    employees: "Сотрудники и смены", payroll: "Расценки и начисления", reports: "Отчеты", notifications: "Уведомления",
    catalogs: "Справочники", organization: "Оргструктура", users: "Пользователи и роли", audit: "Журнал аудита", operations: "Системные задания", settings: "Настройки",
    search: "Поиск по заказу, клиенту или модели", logout: "Выйти", profile: "Профиль", newOrder: "Новый заказ",
    activeOrders: "Активные заказы", overdue: "Просрочено", confirmed: "Подтверждено", clientsTotal: "Клиентов",
    recentOrders: "Последние заказы", allOrders: "Все заказы", loading: "Загрузка…", empty: "Данных пока нет",
    retry: "Повторить", save: "Сохранить", cancel: "Отмена", create: "Создать", edit: "Изменить", approve: "Утвердить",
    filters: "Фильтры", language: "Язык", noAccess: "Нет доступа", noAccessHint: "Для этого раздела не назначено разрешение.",
  },
  uz: {
    dashboard: "Umumiy ko‘rinish", orders: "Buyurtmalar", clients: "Mijozlar", contracts: "Shartnomalar", products: "Modellar va variantlar",
    materials: "Materiallar va yetkazib beruvchilar", bom: "BOM va me’yorlar", requirements: "Ehtiyoj va tanqislik", procurement: "Xaridlar",
    receipts: "Materiallarni qabul qilish", inventory: "Qoldiqlar va zaxiralar", stockOperations: "Ombor operatsiyalari", inventoryCounts: "Inventarizatsiya",
    routings: "Texnologik marshrutlar", productionOrders: "Ishlab chiqarish buyurtmalari", dispatcher: "Dispetcherlik",
    cutting: "Bichish", sewing: "Tikuv", productionWip: "WIP va to‘xtashlar", quality: "Sifat nazorati va qabul qilish",
    defects: "Nuqsonlar", rework: "Qayta ishlash", packaging: "Qadoqlash va QR", finishedGoods: "Tayyor mahsulot", shipments: "Jo‘natmalar",
    employees: "Xodimlar va smenalar", payroll: "Tariflar va hisob-kitoblar", reports: "Hisobotlar", notifications: "Bildirishnomalar",
    catalogs: "Ma’lumotnomalar", organization: "Tashkiliy tuzilma", users: "Foydalanuvchilar va rollar", audit: "Audit jurnali", operations: "Tizim vazifalari", settings: "Sozlamalar",
    search: "Buyurtma, mijoz yoki model bo‘yicha qidirish", logout: "Chiqish", profile: "Profil", newOrder: "Yangi buyurtma",
    activeOrders: "Faol buyurtmalar", overdue: "Muddati o‘tgan", confirmed: "Tasdiqlangan", clientsTotal: "Mijozlar",
    recentOrders: "So‘nggi buyurtmalar", allOrders: "Barcha buyurtmalar", loading: "Yuklanmoqda…", empty: "Hozircha ma’lumot yo‘q",
    retry: "Qayta urinish", save: "Saqlash", cancel: "Bekor qilish", create: "Yaratish", edit: "O‘zgartirish", approve: "Tasdiqlash",
    filters: "Filtrlar", language: "Til", noAccess: "Kirish huquqi yo‘q", noAccessHint: "Bu bo‘lim uchun ruxsat berilmagan.",
  },
} as const;

type Key = keyof typeof dictionary.ru;
type Catalog = Record<string, string>;

const catalog = uzCatalog as Catalog;
const messageCache = new Map<string, string>();

function readStoredLanguage(): Language {
  if (typeof localStorage === "undefined") return "ru";
  return localStorage.getItem("leytenant-language") === "uz" ? "uz" : "ru";
}

function persistLanguage(language: Language) {
  localStorage.setItem("leytenant-language", language);
  document.documentElement.lang = language;
  document.cookie = `django_language=${language}; Path=/; SameSite=Lax`;
}

let activeLanguage: Language = readStoredLanguage();

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const patterns = Object.entries(catalog)
  .filter(([source]) => /\{[^{}]+\}/.test(source))
  .map(([source, target]) => {
    const names = [...source.matchAll(/\{([^{}]+)\}/g)].map((match) => match[1]);
    const expression = source.split(/\{[^{}]+\}/).map(escapeRegExp).join("(.*?)");
    return { source, target, names, expression: new RegExp(`^${expression}$`, "u") };
  })
  .sort((left, right) => right.source.length - left.source.length);

function translateNormalized(message: string): string {
  const direct = catalog[message];
  if (direct) return direct;
  const cached = messageCache.get(message);
  if (cached) return cached;
  for (const pattern of patterns) {
    const match = pattern.expression.exec(message);
    if (!match) continue;
    const values: Record<string, string> = Object.fromEntries(pattern.names.map((name, index) => [name, translateNormalized(match[index + 1])]));
    const translated: string = pattern.target.replace(/\{([^{}]+)\}/g, (_placeholder, name) => values[name] ?? _placeholder);
    messageCache.set(message, translated);
    return translated;
  }
  messageCache.set(message, message);
  return message;
}

export function currentLanguage() {
  return activeLanguage;
}

export function locale() {
  return activeLanguage === "uz" ? "uz-UZ" : "ru-RU";
}

export function translate(message: string) {
  if (!message || activeLanguage === "ru") return message;
  const leading = message.match(/^\s*/u)?.[0] ?? "";
  const trailing = message.match(/\s*$/u)?.[0] ?? "";
  const normalized = message.trim().replace(/\s+/gu, " ");
  return `${leading}${translateNormalized(normalized)}${trailing}`;
}

interface I18nValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: Key) => string;
  translate: (message: string) => string;
  locale: string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = readStoredLanguage();
    activeLanguage = stored;
    if (typeof document !== "undefined") persistLanguage(stored);
    return stored;
  });
  const value = useMemo<I18nValue>(() => ({
    language,
    setLanguage: (next) => {
      activeLanguage = next;
      messageCache.clear();
      persistLanguage(next);
      setLanguageState(next);
    },
    t: (key) => dictionary[language][key],
    translate,
    locale: language === "uz" ? "uz-UZ" : "ru-RU",
  }), [language]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("I18nProvider is missing");
  return value;
}
